/* ─────────────────────────────────────────────────────────────
   gpc-compare.js
   Upload to: Assets/

   Drives both halves of the comparison feature:
     • sections/gpc-compare-showcase.liquid  — the home page picker
     • sections/gpc-compare-table.liquid     — the /pages/compare table

   Loaded GLOBALLY from snippets/head.liquid, NOT from either section:
     <script src="{{ 'gpc-compare.js' | asset_url }}" defer></script>
   A <script> inside a home page section never executes on this storefront —
   it lands in the DOM and is never run or even fetched. Do not move it back.

   WHY THE TABLE IS BUILT IN JAVASCRIPT
   Liquid cannot read a query string, so /pages/compare?ids=a,b,c cannot be
   rendered server-side. Instead the section prints every comparable product
   as JSON — metafields included, which the /products/x.js endpoint does NOT
   return — and this picks the three out of it. Same pattern as build_json in
   the configurator section, which is already proven on this storefront.

   Selection lives in two places on purpose: localStorage so it survives
   moving between pages, and the URL so a comparison can be sent to someone.
   ───────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var KEY = 'gpcCompare';
  var MAX = 3;

  /* ── selection ─────────────────────────────────────────── */
  function read() {
    try {
      var v = JSON.parse(window.localStorage.getItem(KEY) || '[]');
      return Array.isArray(v) ? v.slice(0, MAX) : [];
    } catch (e) { return []; }
  }
  function write(list) {
    try { window.localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX))); } catch (e) {}
  }
  function fromUrl() {
    var m = /[?&]ids=([^&]+)/.exec(window.location.search);
    return m ? decodeURIComponent(m[1]).split(',').filter(Boolean).slice(0, MAX) : [];
  }

  /* ── shared helpers ────────────────────────────────────── */
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function parseJSON(el, what) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      // A single malformed metafield corrupts the whole payload, and the page
      // then looks empty for no visible reason. Say so in the console rather
      // than leaving someone to guess.
      if (window.console) {
        console.error('[gpc-compare] could not read the ' + (what || 'data') +
          ' payload — one of the products in the comparable collection has a ' +
          'metafield that is not valid JSON.', e);
      }
      return null;
    }
  }

  var ICONS = {
    cpu: '<rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><path d="M9 1v3M15 1v3M9 20v3M15 20v3M20 9h3M20 14h3M1 9h3M1 14h3"/>',
    gpu: '<rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="8" cy="12" r="2.6"/><circle cx="16" cy="12" r="2.6"/>',
    ram: '<rect x="2" y="7" width="20" height="10" rx="1.5"/><path d="M6 17v3M10 17v3M14 17v3M18 17v3M7 11h3M14 11h3"/>',
    ssd: '<rect x="2" y="9" width="20" height="7" rx="2"/><path d="M6 12.5h.01M10 12.5h.01"/><path d="M5 9V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v3"/>',
    cooler: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="2"/><path d="M12 3v7M12 14v7M3 12h7M14 12h7"/>',
    mobo: '<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M8 3v4M8 7h4v4"/><circle cx="16" cy="8" r="1.6"/><circle cx="8" cy="16" r="1.6"/><path d="M12 16h5"/>',
    'case': '<rect x="5" y="2" width="14" height="20" rx="2"/><path d="M9 6h6M9 10h6"/><circle cx="12" cy="16" r="2.2"/>',
    colour: '<path d="M12 2.7s6 6.6 6 10.4a6 6 0 0 1-12 0c0-3.8 6-10.4 6-10.4z"/>',
    dims: '<rect x="2" y="8" width="20" height="8" rx="1.6"/><path d="M6.5 8v3M10 8v4M13.5 8v3M17 8v4"/>',
    psu: '<path d="M18.36 6.64a9 9 0 1 1-12.73 0"/><path d="M12 2v10"/>',
    game: '<rect x="2" y="7" width="20" height="11" rx="4"/><path d="M7 12h3M8.5 10.5v3"/><circle cx="15.5" cy="11.5" r="1"/><circle cx="17.5" cy="14" r="1"/>'
  };
  function icon(k) {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + (ICONS[k] || ICONS.cpu) + '</svg>';
  }

  // Mirrors snippets/gpc-compare-card.liquid. Kept in step with it by hand —
  // if the card changes shape there, change it here too.
  function cardHTML(p) {
    var cpu = ((p.specs || {}).cpu || '').replace(/^(AMD|Intel)\s+/, '');
    var topGpu = (p.gpu || [])[(p.gpu || []).length - 1] || '';
    return '<div class="gpcc-card" role="button" tabindex="0" data-gpcc-card data-handle="' + esc(p.h) + '"' +
      ' data-pool="false" data-preselect="false" aria-pressed="false">' +
      '<span class="gpcc-shot">' +
        (p.img ? '<img src="' + esc(p.img) + '" alt="" loading="lazy">' : '<span class="gpcc-noshot"></span>') +
        '<span class="gpcc-tick" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
        'stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>' +
      '</span>' +
      '<span class="gpcc-cname">' + esc(p.t) + '</span>' +
      ((cpu || topGpu)
        ? '<span class="gpcc-spec">' +
            (cpu ? '<span>' + icon('cpu') + '<b>' + esc(cpu) + '</b></span>' : '') +
            (topGpu ? '<span>' + icon('gpu') + '<i>up to</i><b>' + esc(topGpu) + '</b></span>' : '') +
          '</span>'
        : '') +
      '<span class="gpcc-cprice"><em>From</em>' + esc(p.p) + '</span>' +
      '</div>';
  }

  /* ══ 1. THE HOME PAGE PICKER ════════════════════════════ */
  function initWidget(root) {
    if (root.dataset.gpccReady === '1') return;
    root.dataset.gpccReady = '1';

    var grid = root.querySelector('[data-gpcc-grid]');
    var count = root.querySelector('[data-gpcc-count]');
    var go = root.querySelector('[data-gpcc-go]');
    var search = root.querySelector('[data-gpcc-search]');
    var none = root.querySelector('[data-gpcc-none]');
    if (!grid) return;

    // On the compare page the grid arrives empty on purpose: that page already
    // prints every build as JSON for the table, so rendering the same products
    // again in Liquid would ship the data twice. Build the cards from it.
    if (!grid.children.length) {
      var page = root.closest('[data-gpcc-page]') || document;
      var payload = parseJSON(page.querySelector('[data-gpcc-data]'), 'builds') || [];
      grid.innerHTML = payload.map(cardHTML).join('');
    }

    var cards = [].slice.call(grid.querySelectorAll('[data-gpcc-card]'));
    var base = go ? (go.getAttribute('href') || '/pages/compare') : '';
    var inline = root.hasAttribute('data-gpcc-inline');   // the picker on /pages/compare
    var order = [];

    // A build already chosen elsewhere on the site stays chosen here — and on
    // the compare page, whatever the URL asked for.
    var start = fromUrl();
    if (!start.length) start = read();
    start.forEach(function (h) {
      var card = cards.filter(function (c) { return c.dataset.handle === h; })[0];
      if (card) order.push(card);
    });
    if (!order.length) {
      order = cards.filter(function (c) { return c.dataset.preselect === 'true'; }).slice(0, MAX);
    }

    var quiet = false;      // set while re-syncing, so we do not echo back

    /* The rail holds its height when a search empties it, so the section does
       not collapse and spring back as you type.

       Measured from the cards rather than declared. The CSS carried a
       hard-coded min-height written when the card was smaller; the card then
       grew to 270px and nobody re-measured, so a search that found nothing
       took 56px out of the widget on a phone. A number copied from a design
       goes stale in silence — a measurement cannot.

       It only ever grows: too tall by a pixel is invisible, too short is the
       bug this exists to prevent.

       Measured on the GRID, not on the tallest card. The grid carries 4px of
       padding and the theme sets box-sizing: border-box, so a min-height taken
       from the card is 8px short of the height the grid actually occupies when
       the cards are in it — which left a small version of the same jump. */
    var railH = 0;
    function lockHeight() {
      var h = grid.offsetHeight;
      if (h > railH) {
        railH = h;
        grid.style.minHeight = railH + 'px';
      }
    }

    // The rail's own order, captured before anything moves, so any ordering can
    // be rebuilt from scratch rather than nudged from wherever it ended up.
    var natural = cards.slice();

    /* What leads the rail depends on what the shopper is doing.

       Idle — the builds being compared lead, so they are never lost behind a
       scroll while you are choosing.

       Searching — the MATCHES lead. Keeping the selection in front during a
       search is what broke this on a phone: about two cards are visible at
       390px, so three ticked builds filled the rail and every result sat off
       the right-hand edge. You typed, the search worked, and nothing appeared
       to happen. Matches first, selection after; the selection is still there,
       just no longer in the way of the thing you asked for.

       Never called mid-click — cards moving under the cursor is worse than
       scrolling for one. Typing is different: you are looking at the box, not
       the rail. */
    function reorder() {
      var q = search ? search.value.trim().toLowerCase() : '';
      var lead;
      if (q) {
        lead = natural.filter(function (c) {
          return (c.dataset.handle + ' ' + c.textContent).toLowerCase().indexOf(q) > -1;
        });
      } else {
        lead = order.filter(function (c) { return c && c.parentNode === grid; });
      }
      // One fragment, one reflow, whatever the rail is holding.
      var frag = document.createDocumentFragment();
      lead.forEach(function (c) { frag.appendChild(c); });
      natural.forEach(function (c) { if (lead.indexOf(c) < 0) frag.appendChild(c); });
      grid.appendChild(frag);
      grid.scrollLeft = 0;
    }

    function paint() {
      cards.forEach(function (c) {
        var on = order.indexOf(c) > -1;
        c.classList.toggle('is-on', on);
        c.setAttribute('aria-pressed', String(on));
      });
      var n = order.length;
      if (count) count.textContent = n + ' of ' + MAX + ' selected';
      var ids = order.map(function (c) { return c.dataset.handle; });
      if (go) {
        go.textContent = n < 2 ? (go.dataset.empty || 'Pick at least two') :
          (go.dataset.label || 'Compare') + ' ' + n + ' builds';
        go.setAttribute('aria-disabled', n < 2 ? 'true' : 'false');
        go.setAttribute('href', ids.length ? base + '?ids=' + encodeURIComponent(ids.join(',')) : base);
      }
      write(ids);
    }

    grid.addEventListener('click', function (e) {
      var card = e.target.closest('[data-gpcc-card]');
      if (!card) return;
      e.preventDefault();
      var at = order.indexOf(card);
      if (at > -1) {
        order.splice(at, 1);
      } else {
        // A fourth pick replaces the oldest rather than refusing the click —
        // a tick that does nothing reads as broken.
        if (order.length === MAX) order.shift();
        order.push(card);
      }
      paint();
    });

    // role="button" on a div gets the click for free but not the keyboard
    grid.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var card = e.target.closest('[data-gpcc-card]');
      if (!card) return;
      e.preventDefault();
      card.click();
    });

    if (go) {
      go.addEventListener('click', function (e) {
        if (order.length < 2) { e.preventDefault(); return; }
        // On the compare page there is nowhere to navigate to — apply it here.
        if (inline) {
          e.preventDefault();
          document.dispatchEvent(new CustomEvent('gpcc:selection', {
            detail: { ids: order.map(function (c) { return c.dataset.handle; }) }
          }));
          reorder();
        }
      });
    }

    /* Search reaches the whole comparable range, not just the cards on show.
       Pool builds are hidden in CSS until this adds is-found, so they never
       flash up in the moment before the asset loads. */
    function filter() {
      var q = search ? search.value.trim().toLowerCase() : '';
      var shown = 0;
      cards.forEach(function (card) {
        var pool = card.dataset.pool === 'true';
        var on = order.indexOf(card) > -1;
        var hay = (card.dataset.handle + ' ' + card.textContent).toLowerCase();
        var hit = !!q && hay.indexOf(q) > -1;
        // featured: on show unless a search rules it out
        // pool: hidden unless a search finds it
        // picked: on show either way — losing sight of a build you have chosen
        // is how people lose track of the selection
        var show = on || (q ? hit : !pool);
        card.classList.toggle('is-off', !pool && !show);
        card.classList.toggle('is-found', pool && show);
        if (show) shown++;
      });
      if (none) none.hidden = shown > 0;
      // The count stays on the selection and never reports the search. It used
      // to grow to "3 shown, 1 from the wider range", which wrapped in a narrow
      // column and made the section taller on every keystroke.
      if (count) count.textContent = order.length + ' of ' + MAX + ' selected';
      reorder();          // matches to the front — it also resets scrollLeft
      syncArrows();
    }
    if (search) search.addEventListener('input', filter);

    // the table changed its own selection — follow it
    document.addEventListener('gpcc:sync', function (e) {
      var ids = (e.detail && e.detail.ids) || [];
      order = ids.map(function (h) {
        return cards.filter(function (c) { return c.dataset.handle === h; })[0];
      }).filter(Boolean);
      quiet = true;
      paint();
      quiet = false;
      filter();
      reorder();
    });

    /* rail arrows */
    var arrows = [].slice.call(root.querySelectorAll('[data-gpcc-arrow]'));
    function syncArrows() {
      var max = grid.scrollWidth - grid.clientWidth;
      arrows.forEach(function (b) {
        var dir = parseInt(b.getAttribute('data-gpcc-arrow'), 10);
        b.hidden = max <= 4;
        b.disabled = dir < 0 ? grid.scrollLeft <= 2 : grid.scrollLeft >= max - 2;
      });
    }
    arrows.forEach(function (b) {
      b.addEventListener('click', function () {
        var card = grid.querySelector('[data-gpcc-card]:not(.is-off)');
        var step = card ? (card.getBoundingClientRect().width + 10) * 2 : grid.clientWidth * 0.8;
        grid.scrollBy({ left: parseInt(b.getAttribute('data-gpcc-arrow'), 10) * step, behavior: 'smooth' });
      });
    });
    grid.addEventListener('scroll', function () { window.requestAnimationFrame(syncArrows); }, { passive: true });
    window.addEventListener('resize', function () { lockHeight(); syncArrows(); });

    paint();
    lockHeight();     // before filter(), while every card is still on show
    filter();
    reorder();
  }

  /* ══ 2. THE COMPARISON TABLE ════════════════════════════ */
  function initTable(root) {
    if (root.dataset.gpccReady === '1') return;
    root.dataset.gpccReady = '1';

    var all = parseJSON(root.querySelector('[data-gpcc-data]'), 'builds') || [];
    var rows = parseJSON(root.querySelector('[data-gpcc-rows]'), 'rows') || [];
    var mount = root.querySelector('[data-gpcc-table]');
    var empty = root.querySelector('[data-gpcc-empty]');
    var blankH = root.querySelector('[data-gpcc-blank-heading]');
    var blankP = root.querySelector('[data-gpcc-blank-text]');

    // The picker stays on the page whether or not a table is showing — it is
    // how you add a build back after removing one. Only its wording changes.
    function setPicker(hasTable) {
      if (!empty) return;
      empty.hidden = false;
      empty.classList.toggle('is-compact', hasTable);
      var d = empty.dataset;
      if (blankH) blankH.textContent = (hasTable ? d.headingFull : d.headingEmpty) || '';
      if (blankP) blankP.textContent = (hasTable ? d.textFull : d.textEmpty) || '';
    }
    var toggle = root.querySelector('[data-gpcc-diffs]');
    var hint = root.querySelector('[data-gpcc-hint]');
    if (!mount) return;

    var byHandle = {};
    all.forEach(function (p) { byHandle[p.h] = p; });

    // URL first — a shared link must win over whatever this browser had saved.
    var picked = fromUrl().filter(function (h) { return byHandle[h]; });
    if (!picked.length) picked = read().filter(function (h) { return byHandle[h]; });
    picked = picked.slice(0, MAX);

    var chosenGpu = {};   // handle → the card its benchmarks are shown for
    var addLabel = root.dataset.addLabel || 'Add a build';
    var addPlaceholder = root.dataset.addPlaceholder || 'Search builds';

    function announce() {
      document.dispatchEvent(new CustomEvent('gpcc:sync', { detail: { ids: picked } }));
    }
    function pushUrl() {
      var url = window.location.pathname + (picked.length ? '?ids=' + encodeURIComponent(picked.join(',')) : '');
      window.history.replaceState({}, '', url);
    }

    function fmtRow(p, row) {
      var s = p.specs || {};
      switch (row.k) {
        case 'gpu':
          return (p.gpu || []).length
            ? '<span class="gpcc-opts">' + p.gpu.map(function (g) {
                return '<span class="gpcc-chip">' + esc(g) + '</span>'; }).join('') + '</span>'
            : '';
        case 'ram':
          return val(p.ramOpt, s.ram);
        case 'ssd':
          return val(p.ssdOpt, s.ssd);
        case 'colour':
          return s.colour
            ? '<span class="gpcc-swatch"><i style="background:' + esc(s.colourHex || '#888') + '"></i>' + esc(s.colour) + '</span>'
            : '';
        case 'dims':
          return val(s.dims, s.dimsNote);
        default:
          return val(s[row.k], '');
      }
    }
    function val(main, sub) {
      if (!main && !sub) return '';
      return '<span class="gpcc-val">' + esc(main || '') +
        (sub ? '<span class="gpcc-sub">' + esc(sub) + '</span>' : '') + '</span>';
    }

    function benchFor(p, game, res) {
      var b = p.bench || {};
      var g = b[chosenGpu[p.h]] || {};
      var byGame = g[game] || {};
      var v = byGame[res];
      return (typeof v === 'number' && v > 0) ? v : null;
    }

    // Every game any of the chosen builds has a figure for, in the order the
    // first build lists them, so the table is stable rather than alphabetical.
    function gamesShown(list) {
      var seen = [], out = [];
      list.forEach(function (p) {
        Object.keys(p.bench || {}).forEach(function (gpu) {
          Object.keys(p.bench[gpu] || {}).forEach(function (game) {
            if (seen.indexOf(game) === -1) { seen.push(game); out.push(game); }
          });
        });
      });
      return out.filter(function (game) {
        return list.some(function (p) {
          return ['1080p', '1440p', '4k'].some(function (r) { return benchFor(p, game, r); });
        });
      });
    }

    var RES = [{ k: '1080p', label: '1080p' }, { k: '1440p', label: '1440p' }, { k: '4k', label: '4K' }];

    function render() {
      var list = picked.map(function (h) { return byHandle[h]; }).filter(Boolean);
      if (list.length < 2) {
        mount.innerHTML = '';
        setPicker(false);
        if (hint) hint.textContent = '';
        return;
      }
      setPicker(true);

      list.forEach(function (p) {
        if (!chosenGpu[p.h]) {
          var keys = Object.keys(p.bench || {});
          chosenGpu[p.h] = keys.length ? keys[0] : (p.gpu || [])[0] || '';
        }
      });

      // With room for another machine, the table offers the slot rather than
      // making someone scroll back down to the picker to fill it.
      var showAdd = list.length < MAX;
      var addCol = showAdd ? '<col>' : '';
      var addCell = showAdd ? '<td class="gpcc-addcol"></td>' : '';

      var html = '<table class="gpcc-table"><colgroup><col class="gpcc-labelcol">' +
        list.map(function () { return '<col>'; }).join('') + addCol + '</colgroup><thead><tr><th></th>' +
        list.map(function (p) {
          var gpus = Object.keys(p.bench || {});
          return '<th>' +
            (p.img ? '<a class="gpcc-shot" href="' + esc(p.u) + '"><img src="' + esc(p.img) + '" alt="" loading="lazy"></a>' : '') +
            '<a class="gpcc-name" href="' + esc(p.u) + '">' + esc(p.t) + '</a>' +
            '<span class="gpcc-price"><em>From</em>' + esc(p.p) + '</span>' +
            (gpus.length > 1
              ? '<fieldset class="gpcc-pick"><legend>Graphics</legend><div class="gpcc-swatches">' +
                gpus.map(function (g) {
                  return '<label class="gpcc-sw"><input type="radio" name="gpu-' + esc(p.h) + '" value="' + esc(g) + '"' +
                    (g === chosenGpu[p.h] ? ' checked' : '') + ' data-gpcc-gpu="' + esc(p.h) + '"><span>' + esc(g) + '</span></label>';
                }).join('') + '</div></fieldset>'
              : '') +
            '<button type="button" class="gpcc-drop" data-gpcc-drop="' + esc(p.h) + '">Remove</button>' +
            '</th>';
        }).join('') +
        (showAdd
          ? '<th class="gpcc-addcol"><div class="gpcc-addbox">' +
            '<span class="gpcc-addplus" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
            'stroke-width="2.4" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg></span>' +
            '<span class="gpcc-addlab">' + esc(addLabel) + '</span>' +
            '<input type="search" class="gpcc-addsearch" data-gpcc-addsearch placeholder="' + esc(addPlaceholder) + '" ' +
            'aria-label="' + esc(addPlaceholder) + '">' +
            '<ul class="gpcc-addlist" data-gpcc-addlist></ul>' +
            '</div></th>'
          : '') +
        '</tr></thead><tbody>';

      var identical = 0;
      rows.forEach(function (row) {
        var cells = list.map(function (p) { return fmtRow(p, row); });
        if (cells.every(function (c) { return !c; })) return;   // nobody has it
        var same = cells.every(function (c) { return c === cells[0]; });
        if (same) identical++;
        html += '<tr class="gpcc-row' + (same ? ' is-same' : '') + '" data-same="' + (same ? '1' : '0') + '">' +
          '<th scope="row"><span class="gpcc-lab">' + icon(row.k) + '<b>' + esc(row.label) + '</b></span></th>' +
          cells.map(function (c) { return '<td>' + (c || '<span class="gpcc-none">&mdash;</span>') + '</td>'; }).join('') +
          addCell + '</tr>';
      });

      html += '<tr class="gpcc-cta"><th></th>' + list.map(function (p) {
        return '<td><a class="gpcc-btn" href="' + esc(p.u) + '">View build</a>' +
          (p.cfg ? '<a class="gpcc-btn gpcc-btn--ghost" href="' + esc(p.cfg) + '">Configure</a>' : '') + '</td>';
      }).join('') + addCell + '</tr>';

      var games = gamesShown(list);
      if (games.length) {
        html += '<tr class="gpcc-grp"><th></th><td colspan="' + (list.length + (showAdd ? 1 : 0)) + '">' +
          '<span class="gpcc-grplab">Performance</span>' +
          '<span class="gpcc-sub">Average FPS on the graphics card chosen above. A dash means that machine has not been tested at that resolution.</span></td></tr>';
        games.forEach(function (game) {
          var label = game.charAt(0).toUpperCase() + game.slice(1);
          html += '<tr class="gpcc-row gpcc-game"><th scope="row"><span class="gpcc-lab">' + icon('game') +
            '<b>' + esc(label) + '</b><u>' + esc(label.length > 9 ? label.slice(0, 8) : label) + '</u></span></th>' +
            list.map(function (p) {
              return '<td><span class="gpcc-fps">' + RES.map(function (r) {
                var v = benchFor(p, game, r.k);
                return '<span class="gpcc-fpscell"><span class="gpcc-res">' + r.label + '</span>' +
                  (v ? '<b>' + v + '<em>fps</em></b>' : '<span class="gpcc-none">&mdash;</span>') + '</span>';
              }).join('') + '</span></td>';
            }).join('') + addCell + '</tr>';
        });
      }

      mount.innerHTML = html + '</tbody></table>';
      applyDiffs(identical);
    }

    function applyDiffs(identical) {
      var on = toggle && toggle.checked;
      var hidden = 0;
      mount.querySelectorAll('[data-same="1"]').forEach(function (tr) {
        tr.classList.toggle('is-hidden', !!on);
        if (on) hidden++;
      });
      if (hint) {
        hint.textContent = on
          ? hidden + ' identical row' + (hidden === 1 ? '' : 's') + ' hidden.'
          : 'Showing every row. Identical ones are greyed and tagged.';
      }
    }

    root.addEventListener('change', function (e) {
      if (e.target.matches('[data-gpcc-gpu]')) {
        chosenGpu[e.target.getAttribute('data-gpcc-gpu')] = e.target.value;
        render();
      } else if (toggle && e.target === toggle) {
        applyDiffs();
      }
    });

    root.addEventListener('click', function (e) {
      var drop = e.target.closest('[data-gpcc-drop]');
      if (drop) {
        picked = picked.filter(function (h) { return h !== drop.getAttribute('data-gpcc-drop'); });
        write(picked);
        announce();
        pushUrl();   // so a refresh or a share matches the screen
        render();
        return;
      }
      var hit = e.target.closest('[data-gpcc-addpick]');
      if (hit) {
        var h = hit.getAttribute('data-gpcc-addpick');
        if (picked.indexOf(h) === -1 && picked.length < MAX) {
          picked.push(h);
          write(picked);
          announce();
          pushUrl();
          render();
        }
      }
    });

    // Typing in the add column searches the whole comparable range, not just
    // what happens to be on screen.
    root.addEventListener('input', function (e) {
      if (!e.target.matches('[data-gpcc-addsearch]')) return;
      var q = e.target.value.trim().toLowerCase();
      var list = root.querySelector('[data-gpcc-addlist]');
      if (!list) return;
      if (!q) { list.innerHTML = ''; return; }
      var hits = all.filter(function (p) {
        if (picked.indexOf(p.h) > -1) return false;
        var hay = (p.h + ' ' + p.t + ' ' + ((p.specs || {}).cpu || '') + ' ' + (p.gpu || []).join(' ')).toLowerCase();
        return hay.indexOf(q) > -1;
      }).slice(0, 6);
      list.innerHTML = hits.length
        ? hits.map(function (p) {
            return '<li><button type="button" data-gpcc-addpick="' + esc(p.h) + '">' +
              (p.img ? '<img src="' + esc(p.img) + '" alt="" loading="lazy">' : '<span class="gpcc-addnoimg"></span>') +
              '<span><b>' + esc(p.t) + '</b><em>' + esc(p.p) + '</em></span></button></li>';
          }).join('')
        : '<li class="gpcc-addnone">No builds found</li>';
    });

    // the picker inside the empty state announces what was chosen
    document.addEventListener('gpcc:selection', function (e) {
      var ids = (e.detail && e.detail.ids) || [];
      picked = ids.filter(function (h) { return byHandle[h]; }).slice(0, MAX);
      var url = window.location.pathname + (picked.length ? '?ids=' + encodeURIComponent(picked.join(',')) : '');
      window.history.replaceState({}, '', url);
      render();
    });

    render();
  }

  /* ── boot ──────────────────────────────────────────────── */
  function initAll(scope) {
    var found = 0;
    (scope || document).querySelectorAll('[data-gpcc-widget]').forEach(function (el) { initWidget(el); found++; });
    (scope || document).querySelectorAll('[data-gpcc-page]').forEach(function (el) { initTable(el); found++; });
    return found;
  }

  // Confirm which build the store is serving: type  window.GPCC_VERSION  in
  // the console. Undefined means this asset is not loaded — check head.liquid.
  window.GPCC_VERSION = '2026-08-15 compare v3 (rail height)';

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  window.addEventListener('load', function () { initAll(); });

  // Home page sections are injected after parsing on this storefront, so watch
  // briefly for one turning up, then stop.
  if ('MutationObserver' in window) {
    var queued = false;
    var mo = new MutationObserver(function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        if (initAll()) mo.disconnect();
      });
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
    window.setTimeout(function () { mo.disconnect(); }, 15000);
  }

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
